import React from 'react';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import { QuoteTextItem } from '@contracts';
import { TextVariant, Typography } from './Typography';
import { MEDIA } from '@constants';

interface Props {
  data: QuoteTextItem;
  backgroundColor: string;
}

const QuoteBox: React.FC<Props> = ({ data, backgroundColor }) => {
  const image = data.profile_image ? getImage(data.profile_image) : null;
  const nameText = data.name?.text;
  const qualificationText = data.qualification?.text;
  const quoteMainText = data.main_quote?.text;
  const quoteText = data.quote?.text;

  return (
    <Container $backgroundColor={backgroundColor}>
      <TitleWrap>
        <ImageWrap>{image && <GatsbyImage image={image} alt={data.profile_image?.alt ?? ''} objectFit="contain" />}</ImageWrap>
        <TextWrap $hasName={Boolean(nameText)}>
          <Typography $variant={TextVariant.BodyBoldLG}>{nameText}</Typography>
          {qualificationText && <Typography $variant={TextVariant.BodyRegularLG}>{qualificationText}</Typography>}
        </TextWrap>
      </TitleWrap>
      <QuoteTextWrap>
        {quoteMainText && (
          <Typography $variant={TextVariant.Heading6} $mobileVariant={TextVariant.Heading7}>
            {quoteMainText}
          </Typography>
        )}
        {quoteText && (
          <Typography $variant={TextVariant.BodyRegularLG} $mobileVariant={TextVariant.BodyRegularMD}>
            {quoteText}
          </Typography>
        )}
      </QuoteTextWrap>
    </Container>
  );
};

export default QuoteBox;

interface StyleProps {
  $backgroundColor?: string;
}

const Container = styled.div<StyleProps>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  width: 100%;
  max-width: 800px;
  background: ${({ $backgroundColor }) => $backgroundColor || Colors.mint};
  border-radius: 12px;
  padding: 30px 50px 40px 50px;
  transition: all 0.3s ease-in-out;
  will-change: width, padding;

  ${MEDIA.laptopL} {
    min-width: auto;
    width: calc(100% - 24px);
    padding: 30px 20px;
    flex-direction: column;
    justify-content: start;
    align-items: flex-start;
    box-sizing: border-box;
  }
`;

const TitleWrap = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 40px;
  transition: all 0.3s ease-in-out;
  will-change: gap, width;

  ${MEDIA.mobileL} {
    gap: 16px;
    width: 100%;
  }
`;

const TextWrap = styled.div<{ $hasName: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: left;
  transition: all 0.3s ease-in-out;
  will-change: width, margin-top;

  span:last-child {
    margin-bottom: 0;
  }

  ${MEDIA.mobileL} {
    text-align: left;
    width: 100%;
    margin-top: 8px;
  }
`;

const ImageWrap = styled.div`
  width: 100px;
  height: 100px;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all 0.3s ease-in-out;
  will-change: width, height;

  img {
    border-radius: 100%;
    transition: all 0.3s ease-in-out;
  }

  ${MEDIA.laptopL} {
    width: 80px;
    height: 80px;
    margin: 0;
  }
`;

const QuoteTextWrap = styled.div`
  gap: 16px;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease-in-out;
  will-change: width;

  ${MEDIA.mobileL} {
    text-align: left;
    width: 100%;
  }
`;
